/**
 * Step 5 — turn detection. Sensible defaults, tuned by ear, not by us:
 * every threshold reads a localStorage override first so they can be
 * adjusted from devtools without a redeploy.
 *
 *   localStorage.setItem('wizetok_silence_ms', '800')
 *   localStorage.setItem('wizetok_silence_thinking_ms', '1800')
 */

// Live-interview report: candidates speaking for 3-5s would see the answer
// auto-submitted mid-thought, with everything said after that point
// silently dropped. Raised to 3500ms (3.5s) to allow natural pauses while thinking
// without premature cut-offs, coupled with empty STT batch and RMS verification.
export const DEFAULT_SILENCE_MS = 3500;
export const DEFAULT_THINKING_SILENCE_MS = 4000;
// Early-answer grace period: when the candidate has only spoken <= 10 words,
// allow 4.5s for their opening formulation so start-of-thought pauses do not
// prematurely trigger submission and cause repeat questions.
export const EARLY_ANSWER_SILENCE_MS = 4500;
export const EARLY_ANSWER_WORD_LIMIT = 10;
// Hard cap on how long a trailing conjunction can extend the wait — must
// still resolve in bounded time. Step 8's per-state timeout formalizes
// this further; this is the local, turn-level version of the same rule.
export const MAX_SILENCE_EXTENSION_MS = 6000;

export function getConfiguredMs(key, fallback) {
  try {
    const override = window.localStorage.getItem(key);
    const parsed = override != null ? parseInt(override, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  } catch (e) {
    return fallback;
  }
}

// A candidate saying "...and" or "...because" is still going, whatever the
// silence gap looks like. Checked against the trailing word only — cheap,
// and false positives here just mean waiting a bit longer, never cutting
// someone off.
const TRAILING_CONTINUATION_WORDS = new Set([
  'and', 'but', 'or', 'so', 'because', 'since', 'although', 'though',
  'however', 'if', 'when', 'while', 'that', 'which', 'who', 'whose',
  'um', 'uh', 'like', 'well', 'to', 'with', 'for', 'as', 'the', 'a', 'an',
  'is', 'was', 'were', 'not', 'just',
]);

export function endsWithTrailingConjunction(text) {
  const trimmed = (text || '').trim().toLowerCase().replace(/[.,!?]+$/, '');
  if (!trimmed) return false;
  const words = trimmed.split(/\s+/);
  const last = words[words.length - 1];
  return TRAILING_CONTINUATION_WORDS.has(last);
}

// Behavioral / hard questions, or ones that read as open-ended, get more
// room before we assume the candidate is done — thinking pauses are
// longer than between-sentence pauses.
const THINKING_QUESTION_PATTERNS = [
  /\bdescribe a time\b/i,
  /\btell me about a time\b/i,
  /\bwalk me through\b/i,
  /\bgive (me )?an example\b/i,
  /\bhow would you (design|approach|architect|handle)\b/i,
  /\bwhat would you do if\b/i,
  /\bwhy did you choose\b/i,
  /\btrade-?offs?\b/i,
];

export function isThinkingQuestion(question) {
  if (!question) return false;
  if (question.question_type === 'behavioral') return true;
  if ((question.difficulty || '').toLowerCase() === 'hard') return true;
  const text = question.question_text || '';
  return THINKING_QUESTION_PATTERNS.some((p) => p.test(text));
}

export function getSilenceThresholdMs(question) {
  const base = getConfiguredMs('wizetok_silence_ms', DEFAULT_SILENCE_MS);
  const thinking = getConfiguredMs('wizetok_silence_thinking_ms', DEFAULT_THINKING_SILENCE_MS);
  return isThinkingQuestion(question) ? thinking : base;
}

export function getMaxSilenceExtensionMs() {
  return getConfiguredMs('wizetok_silence_max_extension_ms', MAX_SILENCE_EXTENSION_MS);
}

export function getEarlyAnswerSilenceMs() {
  return getConfiguredMs('wizetok_silence_early_answer_ms', EARLY_ANSWER_SILENCE_MS);
}

export function getDynamicSilenceThresholdMs(spokenText, question) {
  const trimmed = (spokenText || '').trim();
  const wordCount = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
  if (wordCount <= EARLY_ANSWER_WORD_LIMIT) {
    return getEarlyAnswerSilenceMs();
  }
  return getSilenceThresholdMs(question);
}
